import { GridFilterItem } from '@mui/x-data-grid-pro';
import { isStringArray } from 'components/common/dataGrid/filterGuards';
import { Identificable } from 'shared/types/commonView';
import { createIdSet } from 'shared/utils/id/entityIds';
import { isDefinedValue } from 'shared/utils/value';

type EntityWithId = Identificable;
type GridRowPredicate<TRow> = (_: unknown, row: TRow) => boolean;
type ScalarComparable = string | number | boolean | null | undefined;

const getSelectedIds = (filterItem: GridFilterItem): Set<string> | null => {
  if (!isStringArray(filterItem.value) || filterItem.value.length === 0) {
    return null;
  }

  return createIdSet(filterItem.value);
};

const createEntityIdMatcher = <T extends EntityWithId>(
  selectedIds: Set<string>
) => {
  return (entity: T | null | undefined): boolean => {
    if (!entity) return false;
    return selectedIds.has(entity._id);
  };
};

const createRowFilterFn = <TRow>(
  filterValue: ScalarComparable,
  getValueFromRow: (row: TRow) => ScalarComparable
): GridRowPredicate<TRow> | null => {
  if (!isDefinedValue(filterValue) || filterValue === '') {
    return null;
  }

  return (_: unknown, row: TRow): boolean => getValueFromRow(row) === filterValue;
};

export const createEntityArrayFilterPredicate = <T extends EntityWithId>(
  filterItem: GridFilterItem
): ((value: T[]) => boolean) | null => {
  const selectedIds = getSelectedIds(filterItem);
  if (!selectedIds) {
    return null;
  }

  const matchesEntityId = createEntityIdMatcher<T>(selectedIds);

  return (value: T[]): boolean => {
    if (!Array.isArray(value)) {
      return false;
    }

    return value.some(matchesEntityId);
  };
};

export const createEntityValueFilterPredicate = <T extends EntityWithId>(
  filterItem: GridFilterItem
): ((value: T | null) => boolean) | null => {
  const selectedIds = getSelectedIds(filterItem);
  if (!selectedIds) {
    return null;
  }

  const matchesEntityId = createEntityIdMatcher<T>(selectedIds);

  return (value: T | null): boolean => matchesEntityId(value);
};

export const createRowValueEqualsFilterPredicate = <
  TRow,
  TFilterValue extends ScalarComparable,
  TRowValue extends ScalarComparable
>(
  filterItem: GridFilterItem,
  getValueFromRow: (row: TRow) => TRowValue,
  isFilterValue: (value: GridFilterItem['value']) => value is TFilterValue
): GridRowPredicate<TRow> | null => {
  if (!isFilterValue(filterItem.value)) {
    return null;
  }

  return createRowFilterFn(
    filterItem.value,
    getValueFromRow
  );
};
